/** @jsx jsx */
import { jsx, Styled, Button } from "theme-ui"
import { Fragment } from "react"
import { LocalizedLink } from "gatsby-theme-i18n"
import { useLocalization } from "gatsby-theme-i18n"

import Container from "../grid/container"
import Col from "../grid/col"
import Row from "../grid/row"

import Panel from "components/panel"
import { Small } from "components/type"

import Facebook from "components/svgs/social/facebook"
import Instagram from "components/svgs/social/instagram"
import Twitter from "components/svgs/social/twitter"
import Youtube from "components/svgs/social/youtube"

import useLocale from "hooks/use-locales"

const styles = {
  row: {
    flexDirection: ["column", "row"],
    alignItems: ["center", "flex-start"],
    textAlign: ["center", "left"],
    justifyContent: ["center", "space-between"],
  },
  col: { width: ["100%", "50%"] },
  box: { mb: 5 },
  heading: { color: "background", mt: 5 },
  link: { color: "background", "&:hover": { textDecoration: "none" } },
  social: {
    color: "background",
    textDecoration: "none",
    mt: 3,
    mb: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: ["center", "flex-start"],
    "&:hover": { textDecoration: "underline" },
  },
  socialIcon: {
    display: "wrap",
    mr: 3,
  },
}

const Footer = () => {
  const { t } = useLocale()
  const { locale } = useLocalization()
  return (
    <footer>
      <Panel bg="secondary" space={[4, 4, 5]}>
        <Container wide>
          <Row sx={styles.row}>
            <Col sx={styles.col}>
              <Styled.h3 as="h2" sx={{ ...styles.heading, mt: 0 }}>
                {t("leadership.title")}
              </Styled.h3>
              <Small>
                <LocalizedLink
                  language={locale}
                  to="/leadership-message"
                  sx={styles.link}
                >
                  {t("leadership.alt")}
                </LocalizedLink>
              </Small>
              <Styled.h3 as="h2" sx={styles.heading}>
                {t("support.title")}
              </Styled.h3>
              <Small>
                <LocalizedLink
                  language={locale}
                  to="/supporting-eachother-1"
                  sx={styles.link}
                >
                  {t("support.one.title")}
                </LocalizedLink>
              </Small>
              <Small>
                <LocalizedLink
                  language={locale}
                  to="/supporting-eachother-2"
                  sx={styles.link}
                >
                  {t("support.two.title")}
                </LocalizedLink>
              </Small>
              <Styled.h3 as="h2" sx={styles.heading}>
                {t("research.title")}
              </Styled.h3>
              <Small>
                <LocalizedLink
                  language={locale}
                  to="/accelerating-research-1"
                  sx={styles.link}
                >
                  {t("research.one.short")}
                </LocalizedLink>
              </Small>
              <Small>
                <LocalizedLink
                  language={locale}
                  to="/accelerating-research-2"
                  sx={styles.link}
                >
                  {t("research.two.short")}
                </LocalizedLink>
              </Small>
              <Small>
                <LocalizedLink
                  language={locale}
                  to="/accelerating-research-3"
                  sx={styles.link}
                >
                  {t("research.three.short")}
                </LocalizedLink>
              </Small>
              <Styled.h3 as="h2" sx={styles.heading}>
                {t("change.title")}
              </Styled.h3>
              <Small>
                <LocalizedLink
                  language={locale}
                  to="/mobilizing-1"
                  sx={styles.link}
                >
                  {t("change.one.title")}
                </LocalizedLink>
              </Small>
              <Small>
                <LocalizedLink
                  language={locale}
                  to="/mobilizing-2"
                  sx={styles.link}
                >
                  {t("change.two.title")}
                </LocalizedLink>
              </Small>
              <Small>
                <LocalizedLink
                  language={locale}
                  to="/mobilizing-3"
                  sx={styles.link}
                >
                  {t("change.three.title")}
                </LocalizedLink>
              </Small>
            </Col>
            <Col sx={styles.col}>
              <Styled.h3 as="h2" sx={{ ...styles.heading, mt: [5, 0] }}>
                {t("partner.title")}
              </Styled.h3>
              <Small>
                <LocalizedLink
                  language={locale}
                  to="/partner-1"
                  sx={styles.link}
                >
                  {t("partner.one.title")}
                </LocalizedLink>
              </Small>
              <Small>
                <LocalizedLink
                  language={locale}
                  to="/partner-2"
                  sx={styles.link}
                >
                  {t("partner.two.title")}
                </LocalizedLink>
              </Small>
              <Small>
                <LocalizedLink
                  language={locale}
                  to="/partner-3"
                  sx={styles.link}
                >
                  {t("partner.three.title")}
                </LocalizedLink>
              </Small>
              <Small>
                <LocalizedLink
                  language={locale}
                  to="/partner-4"
                  sx={styles.link}
                >
                  {t("partner.four.title")}
                </LocalizedLink>
              </Small>
              <Styled.h3 as="h2" sx={styles.heading}>
                {t("impact.title")}
              </Styled.h3>
              <Small>
                <LocalizedLink language={locale} to="/impact" sx={styles.link}>
                  {t("impact.alt")}
                </LocalizedLink>
              </Small>
              <Small>
                <strong>{t("location.title")}</strong>
                <br />
                {t("location.street.one")}
                <br />
                {t("location.street.two")}
              </Small>
              <Small>{t("location.phone")}</Small>
              <Styled.h3 as="h2" sx={styles.heading}>
                {t("social")}
              </Styled.h3>
              <Row sx={{ flexWrap: "wrap" }}>
                <Col sx={{ width: "50%" }}>
                  <Small>
                    <a
                      href="https://www.facebook.com/JDRFCanada"
                      sx={styles.social}
                    >
                      <Facebook sx={styles.socialIcon} />
                      Facebook
                    </a>
                  </Small>
                </Col>
                <Col sx={{ width: "50%" }}>
                  <Small>
                    <a
                      href="https://twitter.com/JDRF_Canada"
                      sx={styles.social}
                    >
                      <Twitter sx={styles.socialIcon} />
                      Twitter
                    </a>
                  </Small>
                </Col>
                <Col sx={{ width: "50%" }}>
                  <Small>
                    <a
                      href="https://instagram.com/jdrf_canada/"
                      sx={styles.social}
                    >
                      <Instagram sx={styles.socialIcon} />
                      Instagram
                    </a>
                  </Small>
                </Col>
                <Col sx={{ width: "50%" }}>
                  <Small>
                    <a
                      href="https://www.youtube.com/user/JDRFCanada"
                      sx={styles.social}
                    >
                      <Youtube sx={styles.socialIcon} />
                      YouTube
                    </a>
                  </Small>
                </Col>
                <Col sx={{ width: ["100%", "100%", "50%"] }}>
                  <Small>
                    <Button as="a" href="https://www.jdrf.ca/" variant="ghost">
                      {t("view.site")}
                    </Button>
                  </Small>
                </Col>
                <Col sx={{ width: ["100%", "100%", "50%"] }}>
                  <Small>
                    <Button
                      as="a"
                      href="mailto:general@jdrf.ca"
                      variant="ghost"
                    >
                      general@jdrf.ca
                    </Button>
                  </Small>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </Panel>
      <Panel bg="primary" space={3}>
        <Container wide>
          <Small sx={{ my: 0, color: "background" }}>
            <Fragment>
              &copy;2020 Juvenile Diabetes Research Foundation, Report by{" "}
              <a
                href="https://floating-point.com/"
                target="_blank"
                rel="noopener noreferrer"
                sx={styles.heading}
              >
                floating-point
              </a>
              .
            </Fragment>
          </Small>
          <Small>
            <strong>{t("charity")}</strong> 11897 6604 RR0001
          </Small>
        </Container>
      </Panel>
    </footer>
  )
}

export default Footer
